import React, { useState, useEffect, useCallback } from "react";
import { Alert, Button, Collapse } from "@mui/material";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import secureStorage from "../../../utils/security/secureStorage";

/**
 * Banner persistente que se muestra cuando el usuario usó un código de
 * recuperación y le quedan pocos disponibles (codesRemaining <= 2).
 *
 * El warning se persiste en secureStorage con la key `mfa_low_codes_warning`
 * desde AuthContext.useRecoveryCodeAuth. El banner permanece visible hasta
 * que:
 *   - El usuario lo cierra explícitamente (dismissed para esta sesión)
 *   - El usuario re-enrola 2FA (disable + activate) y la key se limpia
 *
 * Se renderiza dentro del Layout global, así está presente en cualquier ruta
 * autenticada.
 */
const MfaLowCodesBanner = () => {
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [warning, setWarning] = useState(null);
  const [dismissed, setDismissed] = useState(false);

  const refresh = useCallback(() => {
    const data = secureStorage.getItem("mfa_low_codes_warning");
    setWarning(data && typeof data === "object" ? data : null);
  }, []);

  useEffect(() => {
    refresh();
    // Re-leer cuando la ventana recupera foco (p.ej. tras re-enroll en otra pestaña)
    const onFocus = () => refresh();
    window.addEventListener("focus", onFocus);
    return () => window.removeEventListener("focus", onFocus);
  }, [refresh]);

  const handleDismiss = () => {
    setDismissed(true);
  };

  const handleRegenerate = () => {
    navigate("/UserProfile");
  };

  if (!warning || dismissed) return null;

  const remaining = warning.codesRemaining ?? 0;

  return (
    <Collapse in>
      <Alert
        severity={remaining === 0 ? "error" : "warning"}
        onClose={handleDismiss}
        sx={{
          borderRadius: 0,
          borderBottom: "1px solid",
          borderColor: remaining === 0 ? "error.main" : "warning.main",
        }}
        action={
          <>
            <Button color="inherit" size="small" onClick={handleRegenerate}>
              {t("mfa.bannerRegenerate", "Regenerate 2FA")}
            </Button>
            <Button color="inherit" size="small" onClick={handleDismiss}>
              {t("mfa.bannerDismiss", "Dismiss")}
            </Button>
          </>
        }
      >
        {remaining === 0
          ? t(
              "mfa.bannerNoneLeft",
              "You have NO recovery codes left. If you lose access to your authenticator app, you won't be able to sign in. Please regenerate your 2FA now."
            )
          : t("mfa.bannerFewLeft", {
              count: remaining,
              defaultValue: `You have only ${remaining} recovery code(s) left. Please disable and re-enroll 2FA from your profile to generate a new set of 8 codes.`,
            })}
      </Alert>
    </Collapse>
  );
};

export default MfaLowCodesBanner;
